import React from 'react';
import PropTypes from 'prop-types';
import { LuBoxes, LuCalendarDays, LuChevronRight, LuPaperclip, LuTruck } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { isCargoOverdue } from '../../../utils/companyShipments';
import { DirectionTile } from './cargoUi';
import useCargoText from './useCargoText';

// Satırda gösterilecek en anlamlı tarih: teslim › beklenen › gönderim
const primaryDate = (shipment) => {
  if (shipment.deliveredAt) return { key: 'delivered', value: shipment.deliveredAt };
  if (shipment.expectedAt) return { key: 'expected', value: shipment.expectedAt };
  if (shipment.shippedAt) return { key: 'shipped', value: shipment.shippedAt };
  return null;
};

const META = 'tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1';

/** Kargo satırları. Satıra tıklamak detay panelini açar. */
const CargoList = ({ shipments, showOwner, selectedId, onSelect }) => {
  const { label, directionText, statusText, carrierName, formatDate, formatCost } = useCargoText();

  return (
    <ul className="tw-space-y-2">
      {shipments.map((shipment) => {
        const direction = directionText(shipment.direction);
        const status = statusText(shipment.direction, shipment.status);
        const date = primaryDate(shipment);
        const overdue = isCargoOverdue(shipment);
        const cost = formatCost(shipment.cost);
        const itemCount = (shipment.items || []).length;
        const fileCount = (shipment.attachments || []).length;
        const carrier = carrierName(shipment.carrier);
        const selected = shipment.id === selectedId;
        const badge = <Badge tone={overdue ? 'danger' : status.tone}>{overdue ? label('overdue') : status.label}</Badge>;
        const dateText = date && `${label(`list.dates.${date.key}`)}: ${formatDate(date.value)}`;

        return (
          <li key={shipment.id}>
            <button
              type="button"
              onClick={() => onSelect(shipment)}
              aria-current={selected ? 'true' : undefined}
              className={`tw-group tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-p-3 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 sm:tw-items-center sm:tw-px-4 ${
                selected
                  ? 'tw-border-teal-500/50 tw-bg-teal-500/10 tw-ring-1 tw-ring-teal-500/20 dark:tw-border-teal-400/40 dark:tw-bg-teal-400/10'
                  : 'tw-border-slate-200/80 tw-bg-white/70 hover:tw-border-slate-300 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-border-white/20 dark:hover:tw-bg-white/[0.06]'
              }`}
            >
              <DirectionTile direction={direction.value} title={direction.label} />

              <span className="tw-min-w-0 tw-flex-1 tw-space-y-1">
                <span className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  <span className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{shipment.subject}</span>
                  <span className="sm:tw-hidden">{badge}</span>
                </span>
                <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {direction.party}: {shipment.partyName}
                  {shipment.partyOrg ? ` · ${shipment.partyOrg}` : ''}
                  {showOwner && shipment.ownerName ? ` · ${label('list.owner', { name: shipment.ownerName })}` : ''}
                </span>
                <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-pt-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  <span className={META}>
                    <LuTruck aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                    <span className="tw-truncate">{carrier || label('list.noCarrier')}</span>
                    {shipment.trackingNumber && (
                      <code className="tw-truncate tw-rounded-md tw-bg-slate-900/[0.05] tw-px-1.5 tw-py-px tw-font-mono tw-text-[11px] tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">
                        {shipment.trackingNumber}
                      </code>
                    )}
                  </span>
                  {dateText && (
                    <span className={`${META} sm:tw-hidden`}>
                      <LuCalendarDays aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                      {dateText}
                    </span>
                  )}
                  {itemCount > 0 && (
                    <span className={META} title={label('list.items', { count: itemCount })}>
                      <LuBoxes aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                      <span className="tw-sr-only">{label('list.items', { count: itemCount })}</span>
                      <span aria-hidden="true">{itemCount}</span>
                    </span>
                  )}
                  {fileCount > 0 && (
                    <span className={META} title={label('list.files', { count: fileCount })}>
                      <LuPaperclip aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />
                      <span className="tw-sr-only">{label('list.files', { count: fileCount })}</span>
                      <span aria-hidden="true">{fileCount}</span>
                    </span>
                  )}
                  {cost && <span className="tw-font-semibold tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{cost}</span>}
                </span>
              </span>

              <span className="tw-hidden tw-shrink-0 tw-flex-col tw-items-end tw-gap-1.5 sm:tw-flex">
                {badge}
                {dateText && <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{dateText}</span>}
              </span>

              <LuChevronRight aria-hidden="true" className="tw-hidden tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-300 tw-transition-transform group-hover:tw-translate-x-0.5 group-hover:tw-text-slate-500 sm:tw-block dark:tw-text-slate-600" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

CargoList.propTypes = {
  shipments: PropTypes.arrayOf(PropTypes.object).isRequired,
  showOwner: PropTypes.bool,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default CargoList;
